import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Wrench, RefreshCw, Loader2, CalendarDays, Phone, Lock, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIN_KEY = "autode_admin_pin";

const STATUSES = ["нова", "потвърдена", "приключена", "отказана"];

const STATUS_STYLE = {
  нова: "bg-[#39FF14]/15 text-[#39FF14] border-[#39FF14]/40",
  потвърдена: "bg-blue-500/15 text-blue-400 border-blue-500/40",
  приключена: "bg-zinc-500/15 text-zinc-400 border-zinc-500/40",
  отказана: "bg-red-500/15 text-red-400 border-red-500/40",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("bg-BG", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

function PinGate({ onUnlock }) {
  const [pin, setPin] = useState("");
  const [checking, setChecking] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) return;
    setChecking(true);
    try {
      await axios.post(`${API}/admin/verify`, { pin });
      sessionStorage.setItem(PIN_KEY, pin);
      onUnlock(pin);
    } catch {
      toast.error("Невалиден ПИН код.");
      setPin("");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-5" data-testid="admin-pin-gate">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-[#0D0D0D] p-8 text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center bg-[#008A22] text-white">
          <Lock size={26} />
        </div>
        <h1 className="font-display text-lg font-extrabold uppercase tracking-tight text-white">
          АУТО<span className="text-[#39FF14]">ДЕ·1</span> Админ
        </h1>
        <p className="mt-2 text-sm text-zinc-500">Въведете ПИН код за достъп до заявките.</p>
        <input
          data-testid="admin-pin-input"
          type="password"
          inputMode="numeric"
          autoFocus
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          placeholder="••••"
          className="mt-6 w-full border border-white/10 bg-[#121212] px-4 py-3 text-center font-display text-2xl tracking-[0.5em] text-white placeholder-zinc-700 outline-none focus:border-[#39FF14]"
        />
        <button
          data-testid="admin-pin-submit"
          type="submit"
          disabled={checking}
          className="green-glow mt-5 flex w-full items-center justify-center gap-2 bg-[#39FF14] px-6 py-3 font-display text-sm font-bold uppercase tracking-widest text-black transition-colors duration-300 hover:bg-[#32E612] disabled:opacity-60"
        >
          {checking ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Влез
        </button>
      </form>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default function Admin() {
  const [pin, setPin] = useState(() => sessionStorage.getItem(PIN_KEY) || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const lock = useCallback(() => {
    sessionStorage.removeItem(PIN_KEY);
    setPin("");
    setItems([]);
  }, []);

  const load = useCallback(async (currentPin) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/appointments`, { headers: { "X-Admin-Pin": currentPin } });
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        lock();
        toast.error("Сесията изтече. Въведете ПИН отново.");
      } else {
        toast.error("Грешка при зареждане на заявките.");
      }
    } finally {
      setLoading(false);
    }
  }, [lock]);

  useEffect(() => {
    if (pin) load(pin);
  }, [pin, load]);

  const changeStatus = async (id, status) => {
    const prev = items;
    setItems(items.map((a) => (a.id === id ? { ...a, status } : a)));
    try {
      await axios.patch(`${API}/appointments/${id}/status`, { status }, { headers: { "X-Admin-Pin": pin } });
      toast.success(`Статусът е променен на „${status}“.`);
    } catch (err) {
      setItems(prev);
      if (err?.response?.status === 401) lock();
      else toast.error("Грешка при смяна на статуса.");
    }
  };

  if (!pin) return <PinGate onUnlock={setPin} />;

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: items.filter((a) => (a.status || "нова") === s).length }), {});

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-page">
      <header className="border-b border-white/10 bg-black/60">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 lg:px-8">
          <div className="flex items-center gap-3">
            <Wrench size={20} className="text-[#39FF14]" />
            <h1 className="font-display text-lg font-extrabold uppercase tracking-tight">
              АУТО<span className="text-[#39FF14]">ДЕ·1</span> — Заявки за час
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => load(pin)}
              className="flex items-center gap-2 border border-[#39FF14]/40 px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors duration-300 hover:bg-[#39FF14]/10"
            >
              <RefreshCw size={14} className={loading ? "animate-spin text-[#39FF14]" : "text-[#39FF14]"} /> Обнови
            </button>
            <button
              data-testid="admin-lock-button"
              onClick={lock}
              className="flex items-center gap-2 border border-white/10 px-4 py-2 text-xs font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-300 hover:border-red-500/50 hover:text-red-400"
            >
              <LogOut size={14} /> Изход
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8 lg:px-8">
        <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {STATUSES.map((s) => (
            <div key={s} className="border border-white/10 bg-[#0D0D0D] p-4" data-testid={`admin-stat-${s}`}>
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">{s}</p>
              <p className="mt-1 font-display text-3xl font-extrabold text-[#39FF14]">{counts[s]}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-zinc-500">
            <Loader2 size={28} className="animate-spin text-[#39FF14]" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-24 text-center text-zinc-500" data-testid="admin-empty-state">Няма записани заявки.</p>
        ) : (
          <div className="overflow-x-auto border border-white/10 bg-[#0D0D0D]">
            <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-appointments-table">
              <thead>
                <tr className="border-b border-white/10 text-xs font-bold uppercase tracking-widest text-zinc-500">
                  <th className="px-4 py-3">Получена</th>
                  <th className="px-4 py-3">Име</th>
                  <th className="px-4 py-3">Телефон</th>
                  <th className="px-4 py-3">Автомобил</th>
                  <th className="px-4 py-3">Услуга</th>
                  <th className="px-4 py-3">Дата</th>
                  <th className="px-4 py-3">Съобщение</th>
                  <th className="px-4 py-3">Статус</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} className="border-b border-white/5 align-top transition-colors hover:bg-white/[0.03]" data-testid={`admin-row-${a.id}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-zinc-400">{fmtDate(a.created_at)}</td>
                    <td className="px-4 py-3 font-semibold">{a.name}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <a href={`tel:${a.phone}`} className="flex items-center gap-1.5 text-[#39FF14] hover:underline">
                        <Phone size={13} /> {a.phone}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-zinc-300">{a.car || "—"}</td>
                    <td className="px-4 py-3 text-zinc-300">{a.service || "—"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-zinc-300">
                      {a.preferred_date ? (
                        <span className="flex items-center gap-1.5"><CalendarDays size={13} className="text-[#39FF14]" /> {a.preferred_date}</span>
                      ) : "—"}
                    </td>
                    <td className="max-w-[220px] px-4 py-3 text-zinc-400">{a.message || "—"}</td>
                    <td className="px-4 py-3">
                      <select
                        data-testid={`admin-status-select-${a.id}`}
                        value={a.status || "нова"}
                        onChange={(e) => changeStatus(a.id, e.target.value)}
                        className={`cursor-pointer border px-2 py-1.5 text-xs font-bold uppercase tracking-wider outline-none [color-scheme:dark] ${STATUS_STYLE[a.status || "нова"]} bg-[#121212]`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s} className="bg-[#121212] text-white">{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}
